import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import {
  LayoutDashboard,
  Bot,
  Users,
  MessageSquare,
  BarChart2,
  CreditCard,
  Settings,
  LogOut,
  Zap,
  Calendar,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/bots", icon: Bot, label: "Chatbots" },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/conversations", icon: MessageSquare, label: "Conversations" },
  { to: "/analytics", icon: BarChart2, label: "Analytics" },
  { to: "/meetings", icon: Calendar, label: "Meetings" },

  { to: "/pricing", icon: CreditCard, label: "Pricing" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

function PlanPill({ plan }) {
  return <span className={`plan-pill plan-${plan}`}>{plan}</span>;
}

function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/login");
  };

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-head)",
            fontSize: "22px",
            fontWeight: 800,
            color: "var(--brand)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Zap size={20} fill="currentColor" /> Wispy
        </div>
      </div>

      {/* Nav */}
      <nav
        style={{
          flex: 1,
          paddingTop: 12,
          paddingBottom: 12,
          overflowY: "auto",
        }}
      >
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
          >
            <Icon size={17} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: "12px", borderTop: "1px solid var(--border)" }}>
        <div
          style={{
            background: "var(--bg3)",
            borderRadius: "var(--radius)",
            padding: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "var(--brand)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name}
              </div>
              <PlanPill plan={user?.plan || "trial"} />
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            style={{
              width: "100%",
              justifyContent: "flex-start",
              color: "var(--text3)",
              gap: 7,
            }}
            onClick={handleLogout}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function AppLayout() {
  const { user } = useAuth();

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-area">
        <header className="topbar">
          <div style={{ flex: 1 }} />
          {user && !user.isEmailVerified && (
            <NavLink
              to="/verify-email"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13,
                color: "var(--amber)",
                background: "rgba(212,136,42,0.1)",
                padding: "6px 14px",
                borderRadius: "var(--radius)",
                textDecoration: "none",
                border: "1px solid rgba(212,136,42,0.25)",
              }}
            >
              ⚠ Verify your email
            </NavLink>
          )}
          <div style={{ fontSize: 13, color: "var(--text3)" }}>
            {user?.email}
          </div>
        </header>
        <main
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div className="page-content fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
